// Metered agent cost as money (11 §Money: integer micros, 1 micro = 0.000001 of the currency).
// Founder review 2026-10-27: almost no run costs a cent, so "$0.00" everywhere read as free.
// Amounts of a whole unit or more keep two decimals ("$12.40"); smaller ones keep two
// significant digits ("$0.00052", "$0.05"); anything under one micro (sums and averages of
// fractional micros) reads "< $0.000001". A run that reported neither cost nor tokens (the
// self-hosted Hermes bridge today) is "not reported", never a misleading zero.

const MICRO = 0.000001;

function currencyFormat(currency: string, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  return new Intl.NumberFormat("en-US", { style: "currency", currency, ...opts });
}

/** The currency's own decimals (JPY 0, USD 2), or 2 for an unknown code. */
function currencyDigits(currency: string): number {
  try {
    return currencyFormat(currency, {}).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

function format(major: number, currency: string | undefined, min: number, max: number): string {
  if (currency) {
    try {
      return currencyFormat(currency, {
        minimumFractionDigits: min,
        maximumFractionDigits: max,
      }).format(major);
    } catch {
      // An unknown currency code falls through to the plain number.
    }
  }
  return major.toLocaleString("en-US", { minimumFractionDigits: min, maximumFractionDigits: max });
}

/** Micros of a currency unit as money: "$12.40", "$0.00052", "< $0.000001", "$0.00". */
export function formatMicros(micros: number, currency: string | undefined): string {
  const major = micros / 1_000_000;
  const abs = Math.abs(major);
  const whole = currency ? Math.min(2, currencyDigits(currency)) : 2;
  if (abs === 0 || abs >= 1) {
    const digits = currency && abs >= 1 ? currencyDigits(currency) : whole;
    return format(major, currency, digits, digits);
  }
  if (abs < MICRO)
    return `${major < 0 ? "> " : "< "}${format(major < 0 ? -MICRO : MICRO, currency, 6, 6)}`;
  // Two significant digits: 0.05 → 2 decimals, 0.00052 → 5, 0.0000031 → 7.
  const digits = Math.max(whole, Math.ceil(-Math.log10(abs)) + 1);
  return format(major, currency, whole, digits);
}

/** "8,120", "12k". */
export function formatTokens(n: number): string {
  return n >= 10_000 ? `${Math.round(n / 1000)}k` : n.toLocaleString("en-US");
}

/** Neither cost nor tokens came back: the runtime did not report usage (self-hosted runs). */
export function usageUnreported(u: {
  cost_micros: number;
  tokens_in: number;
  tokens_out: number;
}): boolean {
  return u.cost_micros === 0 && u.tokens_in + u.tokens_out === 0;
}

export const UNREPORTED_HELP = "Not reported. Self-hosted runs don't send tokens or cost yet.";
